import React, { forwardRef, useContext, useEffect, useRef, useState, useReducer, useMemo } from 'react';
import { usePopoverContext, useMergeRefs, FloatingPortal, StyledPopoverContent, PopoverContent, Popover } from '@redsift/popovers';
import { baseContainer, FocusWithinGroupContext, isComponent, Item, Flexbox, ListboxContext, partitionComponents, FocusWithinGroupActionType, IconButton, EventKey, ListboxActionType, ListboxReducer, FocusWithinGroup, Text, Icon } from '@redsift/design-system';
import { useLocalizedStringFormatter } from '@react-aria/i18n';
import { mdiChevronDown } from '@redsift/icons';
import classNames from 'classnames';
import styled from 'styled-components';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

/**
 * Component style.
 */
const StyledComboboxContentHeader = styled.div`
  ${baseContainer}
`;

const _excluded$3 = ["children", "className"];
const COMPONENT_NAME$b = 'ComboboxContentHeader';
const CLASSNAME$b = 'redsift-combobox-content-header';
const DEFAULT_PROPS$b = {};

/**
 * The ComboboxContentHeader component.
 */
const ComboboxContentHeader = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$3);
  return /*#__PURE__*/React.createElement(StyledComboboxContentHeader, _extends({}, forwardedProps, {
    className: classNames(ComboboxContentHeader.className, className),
    ref: ref
  }), children);
});
ComboboxContentHeader.className = CLASSNAME$b;
ComboboxContentHeader.defaultProps = DEFAULT_PROPS$b;
ComboboxContentHeader.displayName = COMPONENT_NAME$b;

/**
 * Component style.
 */
const StyledComboboxContentFooter = styled.div`
  ${baseContainer}
`;

const _excluded$2 = ["children", "className"];
const COMPONENT_NAME$a = 'ComboboxContentFooter';
const CLASSNAME$a = 'redsift-combobox-content-footer';
const DEFAULT_PROPS$a = {};

/**
 * The ComboboxContentFooter component.
 */
const ComboboxContentFooter = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$2);
  return /*#__PURE__*/React.createElement(StyledComboboxContentFooter, _extends({}, forwardedProps, {
    className: classNames(ComboboxContentFooter.className, className),
    ref: ref
  }), children);
});
ComboboxContentFooter.className = CLASSNAME$a;
ComboboxContentFooter.defaultProps = DEFAULT_PROPS$a;
ComboboxContentFooter.displayName = COMPONENT_NAME$a;

const _excluded$1 = ["children", "className"];
const COMPONENT_NAME$9 = 'ComboboxContentListbox';
const CLASSNAME$9 = 'redsift-combobox-content-listbox';
const DEFAULT_PROPS$9 = {};

/**
 * The ComboboxContentListbox component.
 */
const ComboboxContentListbox = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      children,
      className
    } = props,
    forwardedProps = _objectWithoutProperties(props, _excluded$1);
  const focusContext = useContext(FocusWithinGroupContext);
  useEffect(() => {
    if (focusContext && focusContext.state.delayedAction && focusContext.state.tabStops.length) {
      focusContext.dispatch(focusContext.state.delayedAction);
    }
  });
  const renderedChildren = [];
  if (Array.isArray(children) && focusContext.state.maxOptionsLength) {
    for (const child of children) {
      if (isComponent(Item)(child)) {
        if (renderedChildren.length <= focusContext.state.maxOptionsLength) {
          if (!focusContext.state.filter) {
            renderedChildren.push(child);
          } else {
            const {
              value: filterValue,
              type,
              caseSensitive
            } = focusContext.state.filter;
            if (!filterValue) {
              renderedChildren.push(child);
            } else if (caseSensitive && (type === 'startsWith' && child.props.value.startsWith(filterValue) || type === 'contains' && child.props.value.includes(filterValue) || type === 'endsWith' && child.props.value.endsWith(filterValue))) {
              renderedChildren.push(child);
            } else if (!caseSensitive && (type === 'startsWith' && child.props.value.toLowerCase().startsWith(filterValue.toLowerCase()) || type === 'contains' && child.props.value.toLowerCase().includes(filterValue.toLowerCase()) || type === 'endsWith' && child.props.value.toLowerCase().endsWith(filterValue.toLowerCase()))) {
              renderedChildren.push(child);
            }
          }
        }
      } else {
        renderedChildren.push(child);
      }
    }
  }
  return /*#__PURE__*/React.createElement(Flexbox, _extends({
    className: classNames(ComboboxContentListbox.className, className)
  }, forwardedProps, {
    ref: ref,
    flexDirection: "column",
    gap: "0px",
    margin: "8px 0px",
    style: {
      outline: 'none'
    },
    width: "100%"
  }), Array.isArray(children) && focusContext.state.maxOptionsLength ? renderedChildren : children);
});
ComboboxContentListbox.className = CLASSNAME$9;
ComboboxContentListbox.defaultProps = DEFAULT_PROPS$9;
ComboboxContentListbox.displayName = COMPONENT_NAME$9;

const COMPONENT_NAME$8 = 'ComboboxContent';
const CLASSNAME$8 = 'redsift-combobox-content';
const DEFAULT_PROPS$8 = {};

/**
 * The ComboboxContent component.
 */
const BaseComboboxContent = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    className,
    style
  } = props;
  const {
    getFloatingProps,
    isOpen,
    refs,
    strategy,
    x,
    y
  } = usePopoverContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);
  const listboxState = useContext(ListboxContext);
  const [[header], [listbox], [footer]] = partitionComponents(React.Children.toArray(children), [isComponent('ComboboxContentHeader'), isComponent('ComboboxContentListbox'), isComponent('ComboboxContentFooter')]);
  return /*#__PURE__*/React.createElement(FloatingPortal, {
    id: "redsift-app-container"
  }, isOpen && /*#__PURE__*/React.createElement(StyledPopoverContent, _extends({
    ref: popoverRef,
    style: _objectSpread2({
      position: strategy,
      top: y !== null && y !== void 0 ? y : 0,
      left: x !== null && x !== void 0 ? x : 0
    }, style)
  }, getFloatingProps(props), {
    className: classNames(PopoverContent.className, BaseComboboxContent.className, className),
    "aria-disabled": listboxState.state.isDisabled,
    "aria-multiselectable": listboxState.state.selectionMode === 'multiple'
  }), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    gap: "0px",
    width: "100%"
  }, header, listbox, footer)));
});
BaseComboboxContent.className = CLASSNAME$8;
BaseComboboxContent.defaultProps = DEFAULT_PROPS$8;
BaseComboboxContent.displayName = COMPONENT_NAME$8;
const ComboboxContent = Object.assign(BaseComboboxContent, {
  Header: ComboboxContentHeader,
  Listbox: ComboboxContentListbox,
  Footer: ComboboxContentFooter
});

var collapse$1 = "Collapse";
var expand$1 = "Expand";
var enUS = {
	collapse: collapse$1,
	expand: expand$1
};

var collapse = "Réduire";
var expand = "Développer";
var frFR = {
	collapse: collapse,
	expand: expand
};

var intlMessages = {
  'en-US': enUS,
  'fr-FR': frFR
};

const ComboboxContext = /*#__PURE__*/React.createContext(null);

const _excluded = ["aria-controls", "aria-expanded", "aria-haspopup", "role"];
const COMPONENT_NAME$7 = 'ComboboxTrigger';
const CLASSNAME$7 = 'redsift-combobox-trigger';
const DEFAULT_PROPS$7 = {};

/**
 * The ComboboxTrigger component.
 */
const ComboboxTrigger = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    canHaveFreeText,
    children,
    hideExpandButton,
    openOnFocus
  } = props;
  const stringFormatter = useLocalizedStringFormatter(intlMessages);
  const {
    getReferenceProps,
    isOpen,
    handleOpen: handleOpenPopover,
    refs
  } = usePopoverContext();
  const childrenRef = children.ref;
  const inputRef = useRef();
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);
  const focusContext = useContext(FocusWithinGroupContext);
  const comboboxState = useContext(ComboboxContext);
  const listboxState = useContext(ListboxContext);
  const renderedChildren = typeof children === 'function' ? children({
    value: comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.value,
    isOpen
  }) : children;
  const handleChange = value => {
    focusContext.dispatch({
      type: FocusWithinGroupActionType.FILTER_LIST,
      payload: {
        filter: _objectSpread2({
          value: value || ''
        }, comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.filter)
      }
    });
    comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.setInputValue(value);
  };
  const handleKeyDown = event => {
    const code = event.code;
    if (code === 'Escape' || code === 'Tab') {
      if (isOpen) {
        handleOpenPopover(false);
      }
    }
    if (/^.$/u.test(event.key) || code === 'Backspace') {
      if (!isOpen) {
        handleOpenPopover(true);
      }
    }
    if (code === 'ArrowDown') {
      if (!isOpen) {
        handleOpenPopover(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowDown : EventKey.Home,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    }
    if (code === 'ArrowUp') {
      if (!isOpen) {
        handleOpenPopover(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowUp : EventKey.End,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    }
    if (code === 'Enter') {
      if (isOpen && focusContext.state.selectedId) {
        var _focusContext$state$a, _focusContext$state$a2;
        listboxState.dispatch({
          type: ListboxActionType.SET,
          payload: {
            values: [(_focusContext$state$a = (_focusContext$state$a2 = focusContext.state.activedescendant) === null || _focusContext$state$a2 === void 0 ? void 0 : _focusContext$state$a2[1]) !== null && _focusContext$state$a !== void 0 ? _focusContext$state$a : ''],
            activedescendant: focusContext.state.activedescendant
          }
        });
      }
    }
  };
  const handleOpen = () => {
    if (isOpen) {
      handleOpenPopover(false);
    } else {
      var _inputRef$current, _focusContext$state$a3;
      handleOpenPopover(true);
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.FOCUS_ON_LIST,
          payload: {
            id: (_focusContext$state$a3 = focusContext.state.activedescendant) === null || _focusContext$state$a3 === void 0 ? void 0 : _focusContext$state$a3[0],
            filter: _objectSpread2({
              value: ''
            }, comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.filter)
          }
        }
      });
    }
  };
  const handleFocus = () => {
    var _inputRef$current2;
    if (openOnFocus) {
      handleOpen();
    }
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.select();
  };
  const handleBlur = () => {
    if (!canHaveFreeText) {
      comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.setInputValue(listboxState.state.selectedValues[0]);
    }
  };
  const handleClear = () => {
    listboxState.dispatch({
      type: ListboxActionType.SET,
      payload: {
        values: [],
        activedescendant: ['', '']
      }
    });
  };
  useEffect(() => {
    focusContext.dispatch({
      type: FocusWithinGroupActionType.FILTER_LIST,
      payload: {
        filter: _objectSpread2({
          value: listboxState.state.selectedValues[0] || ''
        }, comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.filter)
      }
    });
    comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.setValue(listboxState.state.selectedValues[0]);
    if (listboxState.state.selectedValues.length) {
      comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.setInputValue(listboxState.state.selectedValues[0]);
    }
    handleOpenPopover(false);
  }, [listboxState.state]);
  if (isComponent('TextField')(renderedChildren)) {
    var _props$children, _focusContext$state$a4;
    const _getReferenceProps = getReferenceProps(_objectSpread2(_objectSpread2(_objectSpread2({
        ref: triggerRef
      }, props), renderedChildren.props), {}, {
        children: (_props$children = renderedChildren.props.children) !== null && _props$children !== void 0 ? _props$children : ''
      })),
      {
        'aria-controls': ariaControls,
        'aria-expanded': ariaExpanded,
        'aria-haspopup': ariaHaspopup,
        role
      } = _getReferenceProps,
      forwardedProps = _objectWithoutProperties(_getReferenceProps, _excluded);
    return /*#__PURE__*/React.cloneElement(renderedChildren, _objectSpread2(_objectSpread2({}, forwardedProps), {}, {
      inputProps: {
        'aria-activedescendant': isOpen ? (_focusContext$state$a4 = focusContext.state.activedescendant) === null || _focusContext$state$a4 === void 0 ? void 0 : _focusContext$state$a4[0] : undefined,
        'aria-controls': ariaControls,
        'aria-expanded': ariaExpanded,
        'aria-haspopup': ariaHaspopup,
        onKeyDown: handleKeyDown,
        role
      },
      inputRef,
      isDisabled: comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.isDisabled,
      isInvalid: comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.isInvalid,
      onClear: handleClear,
      onChange: handleChange,
      onBlur: handleBlur,
      onFocus: handleFocus,
      value: comboboxState === null || comboboxState === void 0 ? void 0 : comboboxState.inputValue,
      internal: (value, isDisabled, isInvalid, isRequired) => {
        return /*#__PURE__*/React.createElement(React.Fragment, null, typeof renderedChildren.props.internal === 'function' ? renderedChildren.props.internal(value, isDisabled, isInvalid, isRequired) : renderedChildren.props.internal, hideExpandButton ? null : /*#__PURE__*/React.createElement(IconButton, {
          "aria-label": stringFormatter.format(!isOpen ? 'expand' : 'collapse'),
          color: "question",
          icon: mdiChevronDown,
          onClick: handleOpen,
          isDisabled: isDisabled,
          tabIndex: -1,
          iconProps: {
            style: {
              transition: 'transform 300ms ease-out',
              transform: `rotate(${isOpen ? '-180deg' : '0'})`
            }
          }
        }));
      }
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderedChildren);
});
ComboboxTrigger.className = CLASSNAME$7;
ComboboxTrigger.defaultProps = DEFAULT_PROPS$7;
ComboboxTrigger.displayName = COMPONENT_NAME$7;

const COMPONENT_NAME$6 = 'Combobox';
const CLASSNAME$6 = 'redsift-combobox';
const DEFAULT_PROPS$6 = {
  filter: {
    type: 'startsWith',
    caseSensitive: false
  },
  minWidth: 'trigger-width'
};

/**
 * The Combobox component.
 */
const BaseCombobox = props => {
  const {
    defaultValue,
    description,
    descriptionProps,
    filter,
    isDisabled,
    isInvalid,
    maxOptionsLength,
    onChange,
    value,
    onInputChange,
    inputValue: propsInputValue
  } = props;
  const [selectedValue, setValue] = useState(value || defaultValue || '');
  const [inputValue, setInputValue] = useState(propsInputValue || '');
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);
  useEffect(() => {
    if (propsInputValue) {
      state.setInputValue(propsInputValue);
    }
  }, [propsInputValue]);

  /** Listbox context */
  const [listboxState, listboxDispatch] = useReducer(ListboxReducer, {
    isDisabled,
    selectedValues: [selectedValue]
  });
  const listboxContext = useMemo(() => ({
    state: listboxState,
    dispatch: listboxDispatch
  }), [listboxState]);
  useEffect(() => {
    listboxDispatch({
      type: ListboxActionType.UPDATE_OPTIONS,
      payload: {
        isDisabled: isDisabled || false
      }
    });
  }, [isDisabled]);

  /** Combobox context. */
  const state = {
    filter,
    value: selectedValue,
    inputValue,
    isDisabled: isDisabled || false,
    isInvalid: isInvalid || false,
    setValue(value) {
      if (isDisabled) {
        return;
      }
      const previousValue = selectedValue;
      setValue(value);
      if (value !== previousValue && onChange) {
        onChange(value);
      }
    },
    setInputValue(value) {
      if (isDisabled) {
        return;
      }
      const previousValue = inputValue;
      setInputValue(value);
      if (value !== previousValue && onInputChange) {
        onInputChange(value);
      }
    }
  };
  return /*#__PURE__*/React.createElement(FocusWithinGroup, {
    focusType: "virtual-focus",
    focusOnInit: false,
    maxOptionsLength: maxOptionsLength
  }, /*#__PURE__*/React.createElement(ComboboxContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(ListboxContext.Provider, {
    value: listboxContext
  }, /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0px"
  }, /*#__PURE__*/React.createElement(Popover, _extends({
    overrideDisplayName: {
      content: 'ComboboxContent',
      trigger: 'ComboboxTrigger'
    },
    placement: "bottom-start",
    role: "listbox"
  }, props)), description && typeof description === 'string' ? /*#__PURE__*/React.createElement(Text, _extends({
    marginTop: "8px",
    variant: "caption",
    color: isInvalid ? 'error' : 'darkgrey'
  }, descriptionProps), description) : /*#__PURE__*/React.isValidElement(description) ? description : null))));
};
BaseCombobox.className = CLASSNAME$6;
BaseCombobox.defaultProps = DEFAULT_PROPS$6;
BaseCombobox.displayName = COMPONENT_NAME$6;
const Combobox = Object.assign(BaseCombobox, {
  Trigger: ComboboxTrigger,
  Content: ComboboxContent
});

const COMPONENT_NAME$5 = 'MenuButtonContent';
const CLASSNAME$5 = 'redsift-menu-button-content';
const DEFAULT_PROPS$5 = {};

/**
 * The MenuButtonContent component.
 */
const MenuButtonContent = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    className,
    style
  } = props;
  const {
    getFloatingProps,
    isOpen,
    refs,
    strategy,
    x,
    y
  } = usePopoverContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);
  const focusContext = useContext(FocusWithinGroupContext);
  const listboxState = useContext(ListboxContext);
  useEffect(() => {
    if (focusContext && focusContext.state.delayedAction && focusContext.state.tabStops.length) {
      focusContext.dispatch(focusContext.state.delayedAction);
    }
  }, [focusContext.state.delayedAction, focusContext.state.tabStops.length]);
  return /*#__PURE__*/React.createElement(FloatingPortal, {
    id: "redsift-app-container"
  }, isOpen && /*#__PURE__*/React.createElement(StyledPopoverContent, _extends({
    ref: popoverRef,
    style: _objectSpread2({
      position: strategy,
      top: y !== null && y !== void 0 ? y : 0,
      left: x !== null && x !== void 0 ? x : 0
    }, style)
  }, getFloatingProps(props), {
    className: classNames(PopoverContent.className, MenuButtonContent.className, className),
    "aria-disabled": listboxState.state.isDisabled
  }), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    gap: "8px",
    margin: "8px 0px",
    width: "100%"
  }, children)));
});
MenuButtonContent.className = CLASSNAME$5;
MenuButtonContent.defaultProps = DEFAULT_PROPS$5;
MenuButtonContent.displayName = COMPONENT_NAME$5;

const MenuButtonContext = /*#__PURE__*/React.createContext(null);

const COMPONENT_NAME$4 = 'MenuButtonTrigger';
const CLASSNAME$4 = 'redsift-menu-button-trigger';
const DEFAULT_PROPS$4 = {};

/**
 * The MenuButtonTrigger component.
 */
const MenuButtonTrigger = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children
  } = props;
  const {
    getReferenceProps,
    isOpen,
    handleOpen,
    refs
  } = usePopoverContext();
  const childrenRef = children.ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);
  const focusContext = useContext(FocusWithinGroupContext);
  const menuButtonContext = useContext(MenuButtonContext);
  const renderedChildren = typeof children === 'function' ? children({
    isOpen
  }) : children;
  const handleKeyDown = event => {
    const code = event.code;
    if (code === 'Escape' || code === 'Tab') {
      if (isOpen) {
        handleOpen(false);
      }
    } else if (code === 'ArrowDown') {
      if (!isOpen) {
        handleOpen(true);
      }
      if (focusContext.state.selectedId !== null) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.ArrowDown,
              ctrlKey: event.ctrlKey
            }
          }
        });
      } else {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.Home,
              ctrlKey: event.ctrlKey
            }
          }
        });
      }
      event.preventDefault();
    } else if (code === 'ArrowUp') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowUp : EventKey.Home,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    } else if (code === 'Home') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.Home,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    } else if (code === 'End') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.End,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    } else if (code === 'Enter') {
      if (isOpen && focusContext.state.selectedId) {
        var _document$getElementB;
        (_document$getElementB = document.getElementById(focusContext.state.selectedId)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.click();
      }
    }
  };
  const handleClick = event => {
    event.preventDefault();
    if (isOpen) {
      handleOpen(false);
    } else {
      var _focusContext$state$a, _focusContext$state$a2;
      handleOpen(true);
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.FOCUS_ON_LIST,
          payload: {
            id: (_focusContext$state$a = (_focusContext$state$a2 = focusContext.state.activedescendant) === null || _focusContext$state$a2 === void 0 ? void 0 : _focusContext$state$a2[0]) !== null && _focusContext$state$a !== void 0 ? _focusContext$state$a : ''
          }
        }
      });
    }
  };
  if (isComponent('Button')(renderedChildren) || isComponent('IconButton')(renderedChildren)) {
    var _props$children, _focusContext$state$a3, _menuButtonContext$co, _menuButtonContext$va;
    return /*#__PURE__*/React.cloneElement(renderedChildren, _objectSpread2(_objectSpread2({}, getReferenceProps(_objectSpread2(_objectSpread2(_objectSpread2({
      ref: triggerRef
    }, props), renderedChildren.props), {}, {
      children: (_props$children = renderedChildren.props.children) !== null && _props$children !== void 0 ? _props$children : ''
    }))), {}, {
      'aria-activedescendant': isOpen ? (_focusContext$state$a3 = focusContext.state.activedescendant) === null || _focusContext$state$a3 === void 0 ? void 0 : _focusContext$state$a3[0] : undefined,
      className: classNames(renderedChildren.props.className, menuButtonContext === null || menuButtonContext === void 0 ? void 0 : menuButtonContext.triggerClassName),
      color: (_menuButtonContext$co = menuButtonContext === null || menuButtonContext === void 0 ? void 0 : menuButtonContext.color) !== null && _menuButtonContext$co !== void 0 ? _menuButtonContext$co : renderedChildren.props.color,
      isActive: isOpen,
      isDisabled: menuButtonContext === null || menuButtonContext === void 0 ? void 0 : menuButtonContext.isDisabled,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      role: 'button',
      variant: (_menuButtonContext$va = menuButtonContext === null || menuButtonContext === void 0 ? void 0 : menuButtonContext.variant) !== null && _menuButtonContext$va !== void 0 ? _menuButtonContext$va : renderedChildren.props.variant
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderedChildren);
});
MenuButtonTrigger.className = CLASSNAME$4;
MenuButtonTrigger.defaultProps = DEFAULT_PROPS$4;
MenuButtonTrigger.displayName = COMPONENT_NAME$4;

const COMPONENT_NAME$3 = 'MenuButton';
const CLASSNAME$3 = 'redsift-menu-button';
const DEFAULT_PROPS$3 = {
  minWidth: 'trigger-width'
};

/**
 * The MenuButton component.
 */
const BaseMenuButton = props => {
  const {
    color,
    isDisabled,
    label,
    labelProps,
    description,
    descriptionProps,
    variant,
    triggerClassName
  } = props;

  /** MenuButton context. */
  const state = {
    isDisabled: isDisabled || false,
    color,
    variant,
    triggerClassName
  };
  return /*#__PURE__*/React.createElement(FocusWithinGroup, {
    focusType: "virtual-focus",
    listRole: "menu",
    focusOnInit: false
  }, /*#__PURE__*/React.createElement(MenuButtonContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0px"
  }, label && typeof label === 'string' ? /*#__PURE__*/React.createElement(Text, labelProps, label) : label ? label : null, /*#__PURE__*/React.createElement(Popover, _extends({
    overrideDisplayName: {
      content: 'MenuButtonContent',
      trigger: 'MenuButtonTrigger'
    },
    placement: "bottom-end",
    role: "menu"
  }, props)), description && typeof description === 'string' ? /*#__PURE__*/React.createElement(Text, _extends({
    marginTop: "8px",
    variant: "caption",
    color: "darkgrey"
  }, descriptionProps), description) : description ? description : null)));
};
BaseMenuButton.className = CLASSNAME$3;
BaseMenuButton.defaultProps = DEFAULT_PROPS$3;
BaseMenuButton.displayName = COMPONENT_NAME$3;
const MenuButton = Object.assign(BaseMenuButton, {
  Trigger: MenuButtonTrigger,
  Content: MenuButtonContent
});

const COMPONENT_NAME$2 = 'SelectContent';
const CLASSNAME$2 = 'redsift-select-content';
const DEFAULT_PROPS$2 = {};

/**
 * The SelectContent component.
 */
const SelectContent = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    className,
    style
  } = props;
  const {
    getFloatingProps,
    isOpen,
    refs,
    strategy,
    x,
    y
  } = usePopoverContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);
  const focusContext = useContext(FocusWithinGroupContext);
  const listboxState = useContext(ListboxContext);
  useEffect(() => {
    if (focusContext && focusContext.state.delayedAction && focusContext.state.tabStops.length) {
      focusContext.dispatch(focusContext.state.delayedAction);
    }
  }, [focusContext.state.delayedAction, focusContext.state.tabStops.length]);
  return /*#__PURE__*/React.createElement(FloatingPortal, {
    id: "redsift-app-container"
  }, isOpen && /*#__PURE__*/React.createElement(StyledPopoverContent, _extends({
    ref: popoverRef,
    style: _objectSpread2({
      position: strategy,
      top: y !== null && y !== void 0 ? y : 0,
      left: x !== null && x !== void 0 ? x : 0
    }, style)
  }, getFloatingProps(props), {
    className: classNames(PopoverContent.className, SelectContent.className, className),
    "aria-disabled": listboxState.state.isDisabled,
    "aria-multiselectable": listboxState.state.selectionMode === 'multiple'
  }), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    gap: "8px",
    margin: "8px 0px",
    width: "100%"
  }, children)));
});
SelectContent.className = CLASSNAME$2;
SelectContent.defaultProps = DEFAULT_PROPS$2;
SelectContent.displayName = COMPONENT_NAME$2;

const SelectContext = /*#__PURE__*/React.createContext(null);

const COMPONENT_NAME$1 = 'SelectTrigger';
const CLASSNAME$1 = 'redsift-select-trigger';
const DEFAULT_PROPS$1 = {};

/**
 * The SelectTrigger component.
 */
const SelectTrigger = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    children,
    hideExpandButton
  } = props;
  const {
    getReferenceProps,
    isOpen,
    handleOpen,
    refs
  } = usePopoverContext();
  const childrenRef = children.ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);
  const focusContext = useContext(FocusWithinGroupContext);
  const selectContext = useContext(SelectContext);
  const listboxState = useContext(ListboxContext);
  const renderedChildren = typeof children === 'function' ? children({
    value: selectContext === null || selectContext === void 0 ? void 0 : selectContext.value,
    isOpen
  }) : children;
  const handleKeyDown = event => {
    const code = event.code;
    if (code === 'Escape') {
      if (isOpen) {
        handleOpen(false);
      }
    } else if (code === 'ArrowDown') {
      var _focusContext$state$a;
      if (!isOpen) {
        handleOpen(true);
      }
      if (focusContext.state.selectedId !== null) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.ArrowDown,
              ctrlKey: event.ctrlKey
            }
          }
        });
      } else if (!((_focusContext$state$a = focusContext.state.activedescendant) !== null && _focusContext$state$a !== void 0 && _focusContext$state$a[0])) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.Home,
              ctrlKey: event.ctrlKey
            }
          }
        });
      } else {
        var _focusContext$state$a2, _focusContext$state$a3;
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.FOCUS_ON_LIST,
            payload: {
              id: (_focusContext$state$a2 = (_focusContext$state$a3 = focusContext.state.activedescendant) === null || _focusContext$state$a3 === void 0 ? void 0 : _focusContext$state$a3[0]) !== null && _focusContext$state$a2 !== void 0 ? _focusContext$state$a2 : ''
            }
          }
        });
      }
      event.preventDefault();
    } else if (code === 'ArrowUp') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowUp : EventKey.Home,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    } else if (code === 'Home') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.Home,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    } else if (code === 'End') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.End,
            ctrlKey: event.ctrlKey
          }
        }
      });
      event.preventDefault();
    } else if (code === 'Enter') {
      if (isOpen && focusContext.state.selectedId) {
        var _focusContext$state$a4, _focusContext$state$a5;
        listboxState.dispatch({
          type: ListboxActionType.SET,
          payload: {
            values: [(_focusContext$state$a4 = (_focusContext$state$a5 = focusContext.state.activedescendant) === null || _focusContext$state$a5 === void 0 ? void 0 : _focusContext$state$a5[1]) !== null && _focusContext$state$a4 !== void 0 ? _focusContext$state$a4 : ''],
            activedescendant: focusContext.state.activedescendant
          }
        });
      }
    }
  };
  const handleClick = event => {
    event.preventDefault();
    if (isOpen) {
      handleOpen(false);
    } else {
      var _focusContext$state$a6, _focusContext$state$a7;
      handleOpen(true);
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.FOCUS_ON_LIST,
          payload: {
            id: (_focusContext$state$a6 = (_focusContext$state$a7 = focusContext.state.activedescendant) === null || _focusContext$state$a7 === void 0 ? void 0 : _focusContext$state$a7[0]) !== null && _focusContext$state$a6 !== void 0 ? _focusContext$state$a6 : ''
          }
        }
      });
    }
  };
  useEffect(() => {
    selectContext === null || selectContext === void 0 ? void 0 : selectContext.setValue(listboxState.state.selectedValues[0]);
    handleOpen(false);
  }, [listboxState.state]);
  if (isComponent('TextField')(renderedChildren)) {
    var _props$children, _focusContext$state$a8, _props$inputProps;
    return /*#__PURE__*/React.cloneElement(renderedChildren, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, getReferenceProps(_objectSpread2(_objectSpread2(_objectSpread2({
      ref: triggerRef
    }, props), renderedChildren.props), {}, {
      children: (_props$children = renderedChildren.props.children) !== null && _props$children !== void 0 ? _props$children : ''
    }))), {}, {
      role: 'combobox',
      'aria-activedescendant': isOpen ? (_focusContext$state$a8 = focusContext.state.activedescendant) === null || _focusContext$state$a8 === void 0 ? void 0 : _focusContext$state$a8[0] : undefined,
      isDisabled: selectContext === null || selectContext === void 0 ? void 0 : selectContext.isDisabled
    }, (selectContext === null || selectContext === void 0 ? void 0 : selectContext.isInvalid) === true && {
      color: 'error'
    }), {}, {
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      isReadOnly: true,
      isActive: isOpen
    }, typeof children !== 'function' && (selectContext === null || selectContext === void 0 ? void 0 : selectContext.value) && {
      value: selectContext.value
    }), {}, {
      style: _objectSpread2(_objectSpread2({}, renderedChildren.props.style), {}, {
        caretColor: 'transparent',
        cursor: 'pointer'
      }),
      inputProps: _objectSpread2(_objectSpread2({}, renderedChildren.props.inputProps), {}, {
        style: _objectSpread2(_objectSpread2({}, (_props$inputProps = renderedChildren.props.inputProps) === null || _props$inputProps === void 0 ? void 0 : _props$inputProps.style), {}, {
          cursor: 'pointer'
        })
      }),
      internal: (value, isDisabled, isInvalid, isRequired) => {
        return /*#__PURE__*/React.createElement(React.Fragment, null, typeof renderedChildren.props.internal === 'function' ? renderedChildren.props.internal(value, isDisabled, isInvalid, isRequired) : renderedChildren.props.internal, hideExpandButton ? null : /*#__PURE__*/React.createElement(Icon, {
          color: isDisabled ? 'no-data' : 'question',
          icon: mdiChevronDown,
          style: {
            transition: 'transform 300ms ease-out',
            transform: `rotate(${isOpen ? '-180deg' : '0'})`,
            cursor: 'pointer'
          }
        }));
      }
    }));
  } else if (isComponent('Button')(renderedChildren)) {
    var _props$children2, _focusContext$state$a9, _props$rightIcon, _props$rightIconProps;
    return /*#__PURE__*/React.cloneElement(renderedChildren, _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, getReferenceProps(_objectSpread2(_objectSpread2(_objectSpread2({
      ref: triggerRef
    }, props), renderedChildren.props), {}, {
      children: (_props$children2 = renderedChildren.props.children) !== null && _props$children2 !== void 0 ? _props$children2 : ''
    }))), {}, {
      role: 'combobox',
      'aria-activedescendant': isOpen ? (_focusContext$state$a9 = focusContext.state.activedescendant) === null || _focusContext$state$a9 === void 0 ? void 0 : _focusContext$state$a9[0] : undefined,
      isDisabled: selectContext === null || selectContext === void 0 ? void 0 : selectContext.isDisabled
    }, (selectContext === null || selectContext === void 0 ? void 0 : selectContext.isInvalid) === true && {
      color: 'error'
    }), {}, {
      onKeyDown: handleKeyDown,
      onClick: handleClick,
      isActive: isOpen
    }, typeof children !== 'function' && (selectContext === null || selectContext === void 0 ? void 0 : selectContext.value) && {
      children: selectContext.value
    }), {}, {
      rightIcon: (_props$rightIcon = renderedChildren.props.rightIcon) !== null && _props$rightIcon !== void 0 ? _props$rightIcon : mdiChevronDown,
      rightIconProps: renderedChildren.props.rightIcon ? renderedChildren.props.rightIconProps : (_props$rightIconProps = renderedChildren.props.rightIconProps) !== null && _props$rightIconProps !== void 0 ? _props$rightIconProps : {
        style: {
          transition: 'transform 300ms ease-out',
          transform: `rotate(${isOpen ? '-180deg' : '0'})`
        }
      }
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderedChildren);
});
SelectTrigger.className = CLASSNAME$1;
SelectTrigger.defaultProps = DEFAULT_PROPS$1;
SelectTrigger.displayName = COMPONENT_NAME$1;

const COMPONENT_NAME = 'Select';
const CLASSNAME = 'redsift-select';
const DEFAULT_PROPS = {
  minWidth: 'trigger-width'
};

/**
 * The Select component.
 */
const BaseSelect = props => {
  const {
    defaultValue,
    isDisabled,
    onChange,
    value,
    description,
    descriptionProps,
    isInvalid
  } = props;
  const [selectedValue, setValue] = useState(value || defaultValue || '');
  useEffect(() => {
    if (value) {
      state.setValue(value);
    }
  }, [value]);

  /** Listbox context */
  const [listboxState, listboxDispatch] = useReducer(ListboxReducer, {
    isDisabled,
    selectedValues: [selectedValue]
  });
  const listboxContext = useMemo(() => ({
    state: listboxState,
    dispatch: listboxDispatch
  }), [listboxState]);
  useEffect(() => {
    listboxDispatch({
      type: ListboxActionType.UPDATE_OPTIONS,
      payload: {
        isDisabled: isDisabled || false
      }
    });
  }, [isDisabled]);

  /** Select context. */
  const state = {
    value: selectedValue,
    isDisabled: isDisabled || false,
    isInvalid: isInvalid || false,
    setValue(value) {
      if (isDisabled) {
        return;
      }
      const previousValue = selectedValue;
      setValue(value);
      if (value !== previousValue && onChange) {
        onChange(value);
      }
    }
  };
  return /*#__PURE__*/React.createElement(FocusWithinGroup, {
    focusType: "virtual-focus",
    focusOnInit: false
  }, /*#__PURE__*/React.createElement(SelectContext.Provider, {
    value: state
  }, /*#__PURE__*/React.createElement(ListboxContext.Provider, {
    value: listboxContext
  }, /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0px"
  }, /*#__PURE__*/React.createElement(Popover, _extends({
    overrideDisplayName: {
      content: 'SelectContent',
      trigger: 'SelectTrigger'
    },
    placement: "bottom-start",
    role: "listbox"
  }, props)), description && typeof description === 'string' ? /*#__PURE__*/React.createElement(Text, _extends({
    marginTop: "8px",
    variant: "caption",
    color: isInvalid ? 'error' : 'darkgrey'
  }, descriptionProps), description) : description ? description : null))));
};
BaseSelect.className = CLASSNAME;
BaseSelect.defaultProps = DEFAULT_PROPS;
BaseSelect.displayName = COMPONENT_NAME;
const Select = Object.assign(BaseSelect, {
  Trigger: SelectTrigger,
  Content: SelectContent
});

export { BaseCombobox, BaseComboboxContent, BaseMenuButton, BaseSelect, Combobox, ComboboxContent, ComboboxContentFooter, ComboboxContentHeader, ComboboxContentListbox, ComboboxTrigger, MenuButton, MenuButtonContent, MenuButtonTrigger, Select, SelectContent, SelectTrigger };
//# sourceMappingURL=index.js.map
